import type { RaRecord } from "ra-core";
import { useMemo } from "react";
import { toSlug } from "@/lib/toSlug";

import {
  useConfigurationContext,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";

/** Ensure every item in a { value, label } array has a value (slug from label). */
const ensureValues = (items: { value?: string; label: string }[] | undefined) =>
  items?.map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Check for duplicate slugs
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

/** The whole settings form as the `configuration` row's `config`. */
export const transformFormValues = (data: Record<string, any>) => ({
  config: {
    title: data.title,
    lightModeLogo: data.lightModeLogo,
    darkModeLogo: data.darkModeLogo,
    currency: data.currency,
    recordTypes: ensureValues(data.recordTypes),
    dealCategories: ensureValues(data.dealCategories),
    taskTypes: ensureValues(data.taskTypes),
    dealStages: ensureValues(data.dealStages),
    dealPipelineStatuses: data.dealPipelineStatuses,
    noteStatuses: ensureValues(data.noteStatuses),
  } as ConfigurationContextValue,
});

export type SettingsConfigKey = keyof ReturnType<
  typeof transformFormValues
>["config"];

/**
 * One section's save, for a section edited by itself in a dialog.
 *
 * The update replaces the whole `config` JSON, so everything outside the
 * section has to be written back as it is stored. `stored` is therefore the
 * row read from the database, not the configuration context: the context
 * fills missing keys with the app defaults, and saving those would turn
 * "never set" into "set to the default" for every other section. LCCRM-86.
 *
 * The two logos are the exception. Both data providers rewrite them on every
 * configuration update and store "" for one that is missing, which would
 * blank the logo, so a logo that was never stored goes out as the form has it.
 */
export const sectionConfigUpdate = (
  stored: Record<string, unknown> | undefined | null,
  data: Record<string, any>,
  keys: readonly SettingsConfigKey[],
) => {
  const edited = transformFormValues(data).config;
  const config: Record<string, unknown> = {
    ...(stored ?? {}),
    ...Object.fromEntries(keys.map((key) => [key, edited[key]])),
  };
  for (const logo of ["lightModeLogo", "darkModeLogo"] as const) {
    if (config[logo] == null && edited[logo] != null) {
      config[logo] = edited[logo];
    }
  }
  return { config };
};

/** The settings form's starting values, from the configuration in use. */
export const useSettingsDefaultValues = () => {
  const config = useConfigurationContext();

  return useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      currency: config.currency,
      recordTypes: config.recordTypes,
      dealCategories: config.dealCategories,
      taskTypes: config.taskTypes,
      dealStages: config.dealStages,
      dealPipelineStatuses: config.dealPipelineStatuses,
      noteStatuses: config.noteStatuses,
    }),
    [config],
  );
};
